import { Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Adsense from '../Ads/Adsense';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import { ExpertCallout } from '../shared/ExpertCallout';
import { RetirementGoalForm } from './Form/RetirementGoalForm';
import {
  calculateRetirementGoal,
  RetirementGoalOptions,
  RetirementGoalResult,
} from './RetirementGoalPage.hooks';
import styles from './RetirementGoalPage.module.css';

function getExpertCallout(result: RetirementGoalResult) {
  if (result.alreadyAchieved) {
    return {
      title: '현재 자산만으로도 목표 달성이 가능합니다',
      body: '추가 납입 없이도 현재 자산을 운용하면 목표에 도달합니다. 다음 단계는 자산 배분 전략을 점검하는 것입니다.',
      cta: { label: '투자가이드에서 자산배분 전략 보기', href: '/guide' },
    };
  }
  if (result.requiredMonthlySaving > 2_000_000) {
    return {
      title: `월 ${formatManwon(result.requiredMonthlySaving)} 납입은 부담스럽습니다`,
      body: '은퇴 나이 3~5년 연장, 목표 자산 하향 조정, 현재 미활용 자산 즉시 투입 — 세 가지를 함께 조정하면 월 납입 부담이 크게 줄어듭니다.',
      cta: { label: '노후 자금 계산기에서 충분한 목표 재계산', href: '/retirement' },
    };
  }
  if (result.requiredMonthlySaving <= 500_000) {
    return {
      title: '지금 당장 시작할 수 있는 목표입니다',
      body: '이 목표는 충분히 달성 가능합니다. 오늘 자동이체를 설정하는 것이 가장 중요한 첫 행동입니다. 시작이 늦어질수록 필요 납입액은 빠르게 올라갑니다.',
      cta: { label: 'ETF 투자 시작하는 법 보기', href: '/guide' },
    };
  }
  return {
    title: '달성 가능한 계획입니다',
    body: '꾸준한 납입이 가장 중요합니다. 자동이체를 설정해 매월 빠짐없이 납입하세요.',
    cta: { label: '복리 시뮬레이터로 성장 확인', href: '/compound' },
  };
}

function RetirementGoalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<RetirementGoalResult>();
  const resultRef = useRef<HTMLDivElement>(null);

  const initialFromQuery = useMemo(() => {
    const goal = searchParams.get('goal');
    return goal ? { targetAsset: Number(goal) } : undefined;
  }, [searchParams]);

  const handleSubmit = useCallback((opts: RetirementGoalOptions) => {
    logEvent('calculator', 'retirement_goal_calculate', 'submit');
    setResult(calculateRetirementGoal(opts));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(() => (result ? getExpertCallout(result) : null), [result]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>은퇴 목표 설계 계산기 — 매월 얼마나 모아야 할까? | 노후든든</title>
        <meta
          name="description"
          content="목표 은퇴 자산을 설정하면 지금 매달 얼마를 모아야 하는지 역산해드립니다."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>목표 설계 계산기</h1>
          <p className={styles.pageDesc}>
            원하는 은퇴 생활을 역산합니다. 목표 자금을 정하고 지금 얼마씩 모아야 하는지 계산해 드립니다.
          </p>
        </div>
        <div className={styles.formCard}>
          <RetirementGoalForm
            initialValues={initialFromQuery}
            onSubmit={handleSubmit}
          />
        </div>
      </Container>

      {result && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              {result.alreadyAchieved ? (
                <div className={styles.achievedBanner}>
                  <p className={styles.achievedBannerTitle}>이미 달성 가능합니다!</p>
                  <p className={styles.achievedBannerBody}>
                    현재 자산이 {result.years}년 후 {formatManwon(result.futureValueOfCurrentAsset)}로 성장해 목표를 달성합니다.
                  </p>
                </div>
              ) : (
                <div className={styles.resultHeroCard}>
                  <p className={styles.resultHeroLabel}>목표 달성을 위한 필요 월 저축액</p>
                  <p className={styles.resultHeroAmount}>
                    {formatManwon(result.requiredMonthlySaving)}
                  </p>
                  <p className={styles.resultHeroSub}>
                    지금부터 {result.years}년간 매월 납입
                  </p>
                </div>
              )}

              {/* Metrics */}
              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>현재 자산 미래 가치</p>
                  <p className={styles.metricValue}>
                    {formatManwon(result.futureValueOfCurrentAsset)}
                  </p>
                  <p className={styles.metricInterpretation}>은퇴 시점에 불어날 현재 자산</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>추가로 필요한 금액</p>
                  <p className={styles.metricValue}>{formatManwon(result.additionalNeeded)}</p>
                  <p className={styles.metricInterpretation}>현재 자산 외에 새로 모을 금액</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>복리로 불어나는 이자</p>
                  <p className={styles.metricValue}>{formatManwon(result.totalInterest)}</p>
                  <p className={styles.metricInterpretation}>돈이 벌어주는 금액</p>
                </div>
              </div>

              {/* Expert callout */}
              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              {/* CTA */}
              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    logEvent('calculator', 'retirement_goal_result_cta_click', '/compound');
                    navigate('/compound');
                  }}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  복리 시뮬레이터로 달성 계획 세우기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="9320394113" />

              <p className={styles.disclaimer}>
                이 계산기는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
                세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default RetirementGoalPage;
